/* eslint-disable react-hooks/exhaustive-deps */
import { useQuery } from "@apollo/client";
import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { isEqualTrackRef, isWeb } from "@livekit/components-core";
import {
  CarouselLayout,
  ConnectionStateToast,
  FocusLayoutContainer,
  GridLayout,
  isTrackReference,
  LayoutContextProvider,
  LiveKitRoomProps,
  LiveKitRoom as OriginalLiveKitRoom,
  RoomAudioRenderer,
  TrackReferenceOrPlaceholder,
  useCreateLayoutContext,
  useLocalParticipant,
  usePinnedTracks,
  useRemoteParticipants,
  useSpeakingParticipants,
  useTracks,
} from "@livekit/components-react";
import { MediaDeviceFailure, RoomEvent, Track } from "livekit-client";
import moment from "moment";
import useDisplayToast from "modules/hooks/use-display-toast";
import Toast from "modules/toasts/toast";
import { getUserFullName } from "modules/utils/Functions";
import React, {
  ComponentType,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import colors from "../../../app/colors";
import { useOverlay } from "../overlay-provider";
import { DRAWER_TYPE } from "../overlay-provider/overlay-provider";
import { useUsers } from "../users-provider";
import CALL_BY_ID_QUERY from "./networking/call-query";
import { VideoCallBottomBar } from "./video-call-bottom-bar";
import { VideoCallParticipantTile } from "./video-call-participant-tile";
import {
  type VideoCallPinControls,
  VideoCallPinControlsProvider,
} from "./video-call-pin-controls";
import {
  VideoCallTopBar,
  type VideoCallAppBarProps,
} from "./video-call-top-bar";

const LiveKitRoom = OriginalLiveKitRoom as ComponentType<LiveKitRoomProps>;

interface VideoCallConferenceProps {
  isFullScreen: boolean;
  endCall: () => void;
  appBar?: VideoCallAppBarProps;
}

const isScreenShareRef = (track: TrackReferenceOrPlaceholder) =>
  track.source === Track.Source.ScreenShare;

/** Camera track (or placeholder) for a participant; never their screen share. */
function findFaceTrack(
  trackList: TrackReferenceOrPlaceholder[],
  identity: string,
): TrackReferenceOrPlaceholder | undefined {
  return trackList.find(
    (t) => t.participant.identity === identity && !isScreenShareRef(t),
  );
}

function trackSid(track: TrackReferenceOrPlaceholder): string | undefined {
  return isTrackReference(track) ? track.publication.trackSid : undefined;
}

/** True when the (possibly stale) pinned reference still corresponds to a live track. */
function isPinStillLive(
  pinned: TrackReferenceOrPlaceholder,
  trackList: TrackReferenceOrPlaceholder[],
): boolean {
  const sid = trackSid(pinned);
  if (sid) {
    return trackList.some((t) => trackSid(t) === sid);
  }
  return trackList.some(
    (t) =>
      t.participant.identity === pinned.participant.identity &&
      t.source === pinned.source,
  );
}

const VideoCallConference: React.FC<VideoCallConferenceProps> = ({
  isFullScreen,
  endCall,
  appBar,
}) => {
  const { displayToast } = useDisplayToast();

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false },
  );

  const layoutContext = useCreateLayoutContext();

  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const speakingParticipants = useSpeakingParticipants();

  /** Remote screen shares we can actually render (subscribed), in track-list order. */
  const remoteScreenShareTracks = useMemo(
    () =>
      tracks
        .filter(isTrackReference)
        .filter(
          (track) =>
            track.publication.source === Track.Source.ScreenShare &&
            !track.participant.isLocal &&
            track.publication.isSubscribed,
        ),
    [tracks],
  );

  const focusTrack = usePinnedTracks(layoutContext)?.[0];
  const carouselTracks = tracks.filter(
    (track) => !isEqualTrackRef(track, focusTrack),
  );

  // A pin the user chose explicitly. Auto-focus never overrides it; it resets when the
  // user unpins or the pinned track goes away.
  const manualPinRef = useRef(false);
  const pinControls = useMemo<VideoCallPinControls>(
    () => ({
      pinTrack: (trackReference) => {
        manualPinRef.current = true;
        layoutContext.pin.dispatch?.({ msg: "set_pin", trackReference });
      },
      unpin: () => {
        manualPinRef.current = false;
        layoutContext.pin.dispatch?.({ msg: "clear_pin" });
      },
    }),
    [layoutContext],
  );

  /** Remote share track sids in the order they arrived; newest last. */
  const remoteShareArrivalRef = useRef<string[]>([]);

  const remoteShareKey = remoteScreenShareTracks
    .map((t) => t.publication.trackSid)
    .join();
  const tracksKey = tracks
    .map((t) => `${t.participant.identity}:${t.source}:${trackSid(t) ?? ""}`)
    .join();

  useEffect(() => {
    const arrivals = remoteShareArrivalRef.current;
    const liveSids = new Set(
      remoteScreenShareTracks.map((t) => t.publication.trackSid),
    );
    const newShares = remoteScreenShareTracks.filter(
      (t) => !arrivals.includes(t.publication.trackSid),
    );
    remoteShareArrivalRef.current = [
      ...arrivals.filter((sid) => liveSids.has(sid)),
      ...newShares.map((t) => t.publication.trackSid),
    ];

    // A pinned track that no longer exists (share stopped, participant left) releases
    // the stage. Fall back to the most recent remaining remote share, if any.
    if (focusTrack && !isPinStillLive(focusTrack, tracks)) {
      manualPinRef.current = false;
      const fallbackSid = remoteShareArrivalRef.current.at(-1);
      const fallback = remoteScreenShareTracks.find(
        (t) => t.publication.trackSid === fallbackSid,
      );
      if (fallback) {
        layoutContext.pin.dispatch?.({
          msg: "set_pin",
          trackReference: fallback,
        });
      } else {
        layoutContext.pin.dispatch?.({ msg: "clear_pin" });
      }
      return;
    }

    if (newShares.length > 0) {
      const newest = newShares[newShares.length - 1];
      const name = newest.participant.name || "Someone";
      displayToast(() => (
        <Toast message={`${name} started sharing their screen`} />
      ));
      // Newest share takes the stage unless the user pinned something themselves.
      if (!manualPinRef.current) {
        layoutContext.pin.dispatch?.({
          msg: "set_pin",
          trackReference: newest,
        });
      }
    }

    // A pinned placeholder (camera off) becomes a real track once video starts.
    if (focusTrack && !isTrackReference(focusTrack)) {
      const updatedFocusTrack = tracks.find(
        (tr) =>
          tr.participant.identity === focusTrack.participant.identity &&
          tr.source === focusTrack.source,
      );
      if (
        updatedFocusTrack !== focusTrack &&
        isTrackReference(updatedFocusTrack)
      ) {
        layoutContext.pin.dispatch?.({
          msg: "set_pin",
          trackReference: updatedFocusTrack,
        });
      }
    }
  }, [remoteShareKey, tracksKey, focusTrack]);

  // ---- Minimized (PiP) stage ----
  const localIdentity = localParticipant.identity;
  const remoteCount = remoteParticipants.length;
  const firstRemoteIdentity = remoteParticipants[0]?.identity;
  const activeRemoteSpeakerIdentity = speakingParticipants.find(
    (p) => p.identity !== localIdentity,
  )?.identity;

  /** Whose face to show when minimized: active remote speaker, else first remote, else me. */
  const collapsedFaceTrack = useMemo(() => {
    if (remoteCount === 0) {
      return findFaceTrack(tracks, localIdentity);
    }
    if (activeRemoteSpeakerIdentity) {
      const speakingTrack = findFaceTrack(tracks, activeRemoteSpeakerIdentity);
      if (speakingTrack) {
        return speakingTrack;
      }
    }
    if (firstRemoteIdentity) {
      return findFaceTrack(tracks, firstRemoteIdentity);
    }
    return undefined;
  }, [
    activeRemoteSpeakerIdentity,
    firstRemoteIdentity,
    localIdentity,
    remoteCount,
    tracks,
  ]);

  /** A remote screen share to fill the minimized window: the pinned one, else the newest. */
  const collapsedShareTrack = useMemo(() => {
    if (
      focusTrack &&
      isScreenShareRef(focusTrack) &&
      !focusTrack.participant.isLocal &&
      remoteScreenShareTracks.some(
        (t) => t.publication.trackSid === trackSid(focusTrack),
      )
    ) {
      return focusTrack;
    }
    const newestSid = remoteShareArrivalRef.current.at(-1);
    return (
      remoteScreenShareTracks.find(
        (t) => t.publication.trackSid === newestSid,
      ) ?? remoteScreenShareTracks.at(-1)
    );
  }, [focusTrack, remoteScreenShareTracks]);

  const collapsedStageTrack = useMemo(() => {
    if (focusTrack && !isScreenShareRef(focusTrack)) {
      return focusTrack;
    }
    return collapsedFaceTrack ?? tracks.find((t) => !isScreenShareRef(t));
  }, [collapsedFaceTrack, focusTrack, tracks]);

  return (
    <div className="lk-video-conference">
      {isWeb() && (
        <LayoutContextProvider value={layoutContext}>
          <VideoCallPinControlsProvider value={pinControls}>
            <div className="lk-video-conference-inner">
              {isFullScreen && (
                <>
                  {appBar ? <VideoCallTopBar {...appBar} /> : null}
                  {!focusTrack && (
                    <div className="lk-grid-layout-wrapper">
                      <GridLayout tracks={tracks}>
                        <VideoCallParticipantTile />
                      </GridLayout>
                    </div>
                  )}
                  {focusTrack && (
                    <div className="lk-focus-layout-wrapper">
                      <FocusLayoutContainer>
                        <CarouselLayout tracks={carouselTracks}>
                          <VideoCallParticipantTile focusOnClick />
                        </CarouselLayout>
                        <VideoCallParticipantTile trackRef={focusTrack} />
                      </FocusLayoutContainer>
                    </div>
                  )}
                  <VideoCallBottomBar endCall={endCall} />
                </>
              )}
              {!isFullScreen && (
                <div className="video-call-collapsed-stage">
                  {collapsedShareTrack ? (
                    <div className="video-call-collapsed-share">
                      <VideoCallParticipantTile
                        trackRef={collapsedShareTrack}
                        compact
                        disableSpeakingIndicator
                      />
                      {collapsedFaceTrack && (
                        <div className="video-call-collapsed-face">
                          <VideoCallParticipantTile
                            trackRef={collapsedFaceTrack}
                            compact
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <GridLayout
                      tracks={collapsedStageTrack ? [collapsedStageTrack] : []}
                    >
                      <VideoCallParticipantTile />
                    </GridLayout>
                  )}
                </div>
              )}
            </div>
          </VideoCallPinControlsProvider>
        </LayoutContextProvider>
      )}
      <RoomAudioRenderer />
      <ConnectionStateToast />
    </div>
  );
};

const MemoizedVideoCallConference = memo(VideoCallConference);

export interface VideoCallPageProps {
  callId: string;
  /**
   * User id for the app bar title when the caller should be shown (e.g. incoming ring).
   * If omitted, `Call.createdUserId` from the query is used.
   */
  presentationUserId?: string;
  borderRadius?: string;
  endCall: () => void;
  isFullScreen: boolean;
  onMinimize: () => void;
}

/** Loads {@link CALL_BY_ID_QUERY} by `callId`, connects LiveKit, and renders the conference UI. */
const VideoCallPage: React.FC<VideoCallPageProps> = ({
  callId,
  presentationUserId,
  borderRadius,
  endCall,
  isFullScreen,
  onMinimize,
}) => {
  const { openDrawer } = useOverlay();
  const { displayToast } = useDisplayToast();
  const { data, error } = useQuery(CALL_BY_ID_QUERY, {
    variables: { id: callId },
    fetchPolicy: "cache-and-network",
  });
  const call = data?.call ?? undefined;

  const onMediaDeviceFailure = useCallback(
    (failure?: MediaDeviceFailure, kind?: MediaDeviceKind) => {
      const device = kind === "videoinput" ? "camera" : "microphone";
      const message =
        failure === MediaDeviceFailure.PermissionDenied
          ? `Your ${device} is blocked. Click the lock/camera icon in the browser's address bar to allow access, then rejoin the call.`
          : `Unable to access your ${device}. Check that it is connected and not in use by another app.`;
      displayToast(() => <Toast message={message} />);
    },
    [displayToast],
  );

  const onRoomError = useCallback(
    (roomError: Error) => {
      // eslint-disable-next-line no-console
      console.error("LiveKit room error", roomError);
      // Surface device/permission problems that arrive as plain errors; other
      // failures (e.g. transient connection errors) are handled by LiveKit's
      // ConnectionStateToast.
      const failure = MediaDeviceFailure.getFailure(roomError);
      if (failure) {
        onMediaDeviceFailure(failure);
      }
    },
    [onMediaDeviceFailure],
  );

  const { getUsersById } = useUsers();

  useEffect(() => {
    if (data && data.call === null) {
      endCall();
    }
  }, [data, endCall]);

  // While ringing, the callee sees who is calling. Otherwise the header names the other
  // participants once they connect (see VideoCallTopBar) and this is the placeholder.
  const fallbackTitle = useMemo(() => {
    if (presentationUserId) {
      return getUserFullName(getUsersById([presentationUserId])[0]);
    }
    return call?.isVideo ? "Video Call" : "Audio Call";
  }, [call?.isVideo, getUsersById, presentationUserId]);

  const [callDuration, setCallDuration] = useState<string | undefined>();

  useEffect(() => {
    if (!call?.createTime) {
      setCallDuration(undefined);
      return;
    }

    const updateCallDuration = () => {
      const now = moment();
      const start = moment(call.createTime);
      const diff = moment.duration(now.diff(start));

      const hours = String(diff.hours()).padStart(2, "0");
      const minutes = String(diff.minutes()).padStart(2, "0");
      const seconds = String(diff.seconds()).padStart(2, "0");

      const hoursDisplay = hours > "00" ? `${hours}:` : "";

      setCallDuration(`${hoursDisplay}${minutes}:${seconds}`);
    };

    updateCallDuration();
    const interval = setInterval(updateCallDuration, 1000);
    return () => clearInterval(interval);
  }, [call?.createTime]);

  const openVideoCallAddParticipants = useCallback(() => {
    if (!call) return;
    openDrawer({
      type: DRAWER_TYPE.VIDEO_CALL_ADD_PARTICIPANTS,
      videoCallId: call.id,
      videoCallAccessUserIds: [...call.accessUserIdList],
    });
  }, [call, openDrawer]);

  const openVideoCallParticipants = useCallback(
    (activeUserIdList: string[]) => {
      if (!call) return;
      openDrawer({
        type: DRAWER_TYPE.VIDEO_CALL_PARTICIPANTS,
        videoCallAccessUserIds: [...call.accessUserIdList],
        videoCallActiveUserIds: activeUserIdList,
      });
    },
    [call, openDrawer],
  );

  if (!call) {
    return (
      <Flex
        flex="1"
        align="center"
        justify="center"
        minH={0}
        direction="column"
        gap={2}
      >
        {error ? (
          <Text fontSize="sm" color={colors.grayMain}>
            Unable to load this call.
          </Text>
        ) : (
          <Spinner />
        )}
      </Flex>
    );
  }

  return (
    <LiveKitRoom
      token={call.token}
      serverUrl={`wss://${call.host}`}
      connect
      audio
      video={call.isVideo}
      onMediaDeviceFailure={onMediaDeviceFailure}
      onError={onRoomError}
      data-lk-theme="default"
      style={{
        borderRadius,
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        flex="1"
        minH={0}
        minW={0}
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <MemoizedVideoCallConference
          isFullScreen={isFullScreen}
          endCall={endCall}
          appBar={
            isFullScreen
              ? {
                  fallbackTitle,
                  durationLabel: callDuration,
                  serverParticipantCount: call.activeUserIdList?.length ?? 0,
                  onMinimize,
                  onOpenAddParticipants: openVideoCallAddParticipants,
                  onOpenCallParticipants: openVideoCallParticipants,
                }
              : undefined
          }
        />
      </Box>
    </LiveKitRoom>
  );
};

export default memo(VideoCallPage);
